import React, { useState } from 'react';
import {
  Card,
  Row,
  Col,
  Typography,
  Button,
  Space,
  Tag,
  Badge,
  Modal,
  Form,
  Input,
  Spin,
  Alert,
  Timeline,
  Tabs
} from 'antd';
import {
  UserOutlined,
  MailOutlined,
  CalendarOutlined,
  SafetyOutlined,
  EditOutlined,
  SaveOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  BellOutlined,
  GlobalOutlined,
  LockOutlined
} from '@ant-design/icons';
import { useAuth } from '../hooks/useAuth';
import { useProfile } from '../hooks/useProfile';
import type { ProfileUpdateData, PasswordFormData } from '../types/profile';
import { updatePassword, reauthenticateWithCredential, EmailAuthProvider, User } from 'firebase/auth';
import { auth } from '@municipality/firebase';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';

// Extended user interface for profile display
interface ExtendedUserProfile {
  uid: string;
  email?: string;
  displayName?: string;
  fullName?: string;
  name?: string;
  surname?: string;
  role?: string;
  emailVerified?: boolean;
  createdAt?: string;
  updatedAt?: string;
  lastLogin?: string;
  metadata?: {
    creationTime?: string | number;
    lastSignInTime?: string | number;
  };
}

const { Title, Text } = Typography;

interface ProfileFormData {
  name: string;
  surname: string;
  email: string;
}

const Profile: React.FC = () => {
  const { t } = useTranslation('profile');
  const { user, loading } = useAuth();
  const { updateUserProfile, loading: profileLoading } = useProfile();
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!user) {
    return (
      <Alert
        message={t('authenticationRequired')}
        description={t('authenticationRequiredDescription')}
        type="warning"
        showIcon
      />
    );
  }

  const handleEditProfile = () => {
    // Split displayName into name and surname if available
    const displayName = userProfile.displayName || userProfile.fullName || '';
    const nameParts = displayName.split(' ');
    const firstName = userProfile.name || nameParts[0] || '';
    const lastName = userProfile.surname || (nameParts.length > 1 ? nameParts.slice(1).join(' ') : '');
    
    form.setFieldsValue({
      name: firstName,
      surname: lastName,
      email: userProfile.email || ''
    });
    setEditModalVisible(true);
  };

  const handleSaveProfile = async (values: ProfileFormData) => {
    try {
      // Calculate displayName from name and surname
      const displayName = `${values.name} ${values.surname}`.trim();
      
      const updateData: ProfileUpdateData = {
        displayName: displayName,
        fullName: displayName
      };
      
      await updateUserProfile(user as User, updateData, {
        name: values.name,
        surname: values.surname
      });
      setEditModalVisible(false);
    } catch {
      // Error is already handled in useProfile hook
    }
  };

  const handleChangePassword = () => {
    passwordForm.resetFields();
    setPasswordModalVisible(true);
  };

  const handleSavePassword = async (values: PasswordFormData) => {
    try {
      setPasswordLoading(true);

      // Get the current Firebase auth user
      const currentUser = auth.currentUser;
      if (!currentUser || !currentUser.email) {
        message.error(t('messages.noAuthenticatedUser'));
        return;
      }

      // Create credential with current password for reauthentication
      const credential = EmailAuthProvider.credential(
        currentUser.email,
        values.currentPassword
      );

      // Reauthenticate the user
      try {
        await reauthenticateWithCredential(currentUser, credential);
      } catch {
        message.error(t('messages.currentPasswordIncorrect'));
        setPasswordLoading(false);
        return;
      }

      // Update to new password
      await updatePassword(currentUser, values.newPassword);
      
      message.success(t('messages.passwordUpdatedSuccess'));
      setPasswordModalVisible(false);
      passwordForm.resetFields();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('messages.passwordUpdateFailed');
      message.error(errorMessage);
    } finally {
      setPasswordLoading(false);
    }
  };

  const getRoleColor = (role: string) => {
    if (role === 'super_admin' || role === 'SUPER_ADMIN') {
      return 'red';
    }
    return 'default';
  };

  const getDisplayRoleLabel = (role: string) => {
    if (role === 'super_admin' || role === 'SUPER_ADMIN') {
      return 'Super Admin';
    }
    return role;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return t('notAvailable');
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return t('notAvailable');
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const userProfile = user as ExtendedUserProfile;
  const userDisplayName = userProfile.displayName || userProfile.fullName || 'Super Admin';
  const userEmail = userProfile.email || 'No email provided';
  const userRole = userProfile.role || 'super_admin';
  
  // Get the account creation date from Firestore or Firebase metadata
  const accountCreatedDate = userProfile.createdAt || 
    (userProfile.metadata?.creationTime ? new Date(userProfile.metadata.creationTime).toISOString() : undefined);
  
  // Get the last updated date from Firestore or Firebase metadata
  const lastUpdatedDate = userProfile.updatedAt || 
    (userProfile.metadata?.lastSignInTime ? new Date(userProfile.metadata.lastSignInTime).toISOString() : undefined);

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header Section */}
      <Row gutter={[24, 24]} style={{ marginBottom: '24px', display: 'flex', alignItems: 'stretch' }}>
        <Col xs={24} sm={24} md={8} lg={6} style={{ display: 'flex' }}>
          <Card 
            style={{ 
              textAlign: 'center',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: 'none',
              borderRadius: '16px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}
            styles={{ body: { padding: '32px 24px', display: 'flex', flexDirection: 'column', justifyContent: 'center', flex: 1 } }}
          >
            <Title level={3} style={{ color: 'white', margin: '8px 0' }}>
              {userDisplayName}
            </Title>
            <Tag 
              color={getRoleColor(userRole)} 
              style={{ 
                fontSize: '14px',
                padding: '4px 12px',
                borderRadius: '20px',
                marginBottom: '16px'
              }}
            >
              {getDisplayRoleLabel(userRole)}
            </Tag>
            <div style={{ marginTop: '16px' }}>
              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={handleEditProfile}
                style={{
                  borderRadius: '20px',
                  background: 'rgba(255,255,255,0.2)',
                  border: '1px solid rgba(255,255,255,0.3)',
                  color: 'white'
                }}
              >
                {t('editProfile')}
              </Button>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={24} md={16} lg={18} style={{ display: 'flex' }}>
          <Row gutter={[24, 24]} style={{ width: '100%', display: 'flex' }}>
            <Col xs={24} lg={12} style={{ display: 'flex' }}>
              <Card 
                title={
                  <Space>
                    <UserOutlined />
                    {t('personalInformation')}
                  </Space>
                }
                style={{ borderRadius: '12px', width: '100%' }}
              >
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                  <div>
                    <Text type="secondary" style={{ fontSize: '14px' }}>{t('fullName')}</Text>
                    <br />
                    <Text strong style={{ fontSize: '16px' }}>{userDisplayName}</Text>
                  </div>
                  <div>
                    <Text type="secondary" style={{ fontSize: '14px' }}>{t('email')}</Text>
                    <br />
                    <Text copyable style={{ fontSize: '16px' }}>{userEmail}</Text>
                  </div>
                  <div>
                    <Text type="secondary" style={{ fontSize: '14px' }}>{t('role')}</Text>
                    <br />
                    <Tag color={getRoleColor(userRole)} style={{ fontSize: '14px', marginTop: '4px' }}>
                      {getDisplayRoleLabel(userRole)}
                    </Tag>
                  </div>
                </Space>
              </Card>
            </Col>

            <Col xs={24} lg={12} style={{ display: 'flex' }}>
              <Card 
                title={
                  <Space>
                    <CalendarOutlined />
                    {t('accountDetails')}
                  </Space>
                }
                style={{ borderRadius: '12px', width: '100%' }}
              >
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                  <div>
                    <Text type="secondary" style={{ fontSize: '14px' }}>{t('accountCreated')}</Text>
                    <br />
                    <Text style={{ fontSize: '16px' }}>{formatDate(accountCreatedDate)}</Text>
                  </div>
                  <div>
                    <Text type="secondary" style={{ fontSize: '14px' }}>{t('lastUpdated')}</Text>
                    <br />
                    <Text style={{ fontSize: '16px' }}>{formatDate(lastUpdatedDate)}</Text>
                  </div>
                </Space>
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>

      {/* Main Content */}
      <Tabs 
        defaultActiveKey="security" 
        size="large"
        style={{ 
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          padding: '24px'
        }}
        tabBarStyle={{ marginBottom: '24px' }}
        items={[
          {
            key: 'security',
            label: t('security'),
            children: (
          <Row gutter={[24, 24]}>
            <Col xs={24} lg={12}>
              <Card 
                title={
                  <Space>
                    <SafetyOutlined />
                    {t('securitySettings')}
                  </Space>
                }
                style={{ borderRadius: '12px' }}
              >
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                  <div>
                    <Text strong>{t('password')}</Text>
                    <br />
                    <Text type="secondary">{t('lastChanged')}: {formatDate(lastUpdatedDate)}</Text>
                    <br />
                    <Button type="link" size="small" style={{ padding: 0 }} onClick={handleChangePassword}>
                      {t('changePassword')}
                    </Button>
                  </div>
                </Space>
              </Card>
            </Col>

            <Col xs={24} lg={12}>
              <Card 
                title={
                  <Space>
                    <ClockCircleOutlined />
                    {t('recentActivity')}
                  </Space>
                }
                style={{ borderRadius: '12px' }}
              >
                <Timeline
                  items={[
                    {
                      dot: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
                      color: 'green',
                      children: (
                        <>
                          <Text strong>{t('lastLogin')}</Text>
                          <br />
                          <Text type="secondary">{formatDate(user.lastLogin)}</Text>
                        </>
                      )
                    },
                    {
                      dot: <UserOutlined />,
                      color: 'blue',
                      children: (
                        <>
                          <Text strong>{t('profileCreated')}</Text>
                          <br />
                          <Text type="secondary">{formatDate(accountCreatedDate)}</Text>
                        </>
                      )
                    }
                  ]}
                />
              </Card>
            </Col>
          </Row>
            )
          },
          {
            key: 'preferences',
            label: t('preferences'),
            children: (
          <Row gutter={[24, 24]}>
            <Col xs={24} lg={12}>
              <Card 
                title={
                  <Space>
                    <BellOutlined />
                    {t('notifications')}
                  </Space>
                }
                style={{ borderRadius: '12px' }}
              >
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <Text strong>{t('emailNotifications')}</Text>
                      <br />
                      <Text type="secondary">{t('receiveUpdatesViaEmail')}</Text>
                    </div>
                    <Badge status="success" text={t('enabled')} />
                  </div>
                  
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <Text strong>{t('pushNotifications')}</Text>
                      <br />
                      <Text type="secondary">{t('receiveBrowserNotifications')}</Text>
                    </div>
                    <Badge status="default" text={t('disabled')} />
                  </div>
                </Space>
              </Card>
            </Col>

            <Col xs={24} lg={12}>
              <Card 
                title={
                  <Space>
                    <GlobalOutlined />
                    {t('languageAndRegion')}
                  </Space>
                }
                style={{ borderRadius: '12px' }}
              >
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <div>
                    <Text strong>{t('language')}</Text>
                    <br />
                    <Text type="secondary">{t('englishUS')}</Text>
                    <br />
                    <Button type="link" size="small" style={{ padding: 0 }}>
                      {t('changeLanguage')}
                    </Button>
                  </div>
                  
                  <div>
                    <Text strong>{t('timezone')}</Text>
                    <br />
                    <Text type="secondary">{t('utc2EuropeAthens')}</Text>
                    <br />
                    <Button type="link" size="small" style={{ padding: 0 }}>
                      {t('changeTimezone')}
                    </Button>
                  </div>
                </Space>
              </Card>
            </Col>
          </Row>
            )
          }
        ]}
      />

      {/* Edit Profile Modal */}
      <Modal
        title={
          <Space>
            <EditOutlined />
            {t('editProfileModal.title')}
          </Space>
        }
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        footer={null}
        width={500}
        style={{ top: 20 }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveProfile}
          style={{ marginTop: '24px' }}
        >
          <Form.Item
            name="name"
            label={t('editProfileModal.firstName')}
            rules={[
              { required: true, message: t('editProfileModal.firstNameRequired') },
              { min: 2, message: t('editProfileModal.firstNameMin') }
            ]}
          >
            <Input 
              prefix={<UserOutlined />}
              placeholder={t('editProfileModal.firstNamePlaceholder')}
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="surname"
            label={t('editProfileModal.lastName')}
            rules={[
              { required: true, message: t('editProfileModal.lastNameRequired') },
              { min: 2, message: t('editProfileModal.lastNameMin') }
            ]}
          >
            <Input 
              prefix={<UserOutlined />}
              placeholder={t('editProfileModal.lastNamePlaceholder')}
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="email"
            label={t('editProfileModal.email')}
            rules={[
              { required: true, message: t('editProfileModal.emailRequired') },
              { type: 'email', message: t('editProfileModal.emailInvalid') }
            ]}
          >
            <Input 
              prefix={<MailOutlined />}
              placeholder={t('editProfileModal.emailPlaceholder')}
              size="large"
              disabled
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button 
                type="primary" 
                htmlType="submit"
                loading={profileLoading}
                icon={<SaveOutlined />}
              >
                {t('editProfileModal.saveChanges')}
              </Button>
              <Button 
                onClick={() => setEditModalVisible(false)}
                disabled={profileLoading}
              >
                {t('editProfileModal.cancel')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Change Password Modal */}
      <Modal
        title={
          <Space>
            <LockOutlined />
            {t('changePasswordModal.title')}
          </Space>
        }
        open={passwordModalVisible}
        onCancel={() => setPasswordModalVisible(false)}
        footer={null}
        width={500}
        style={{ top: 20 }}
      >
        <Form
          form={passwordForm}
          layout="vertical"
          onFinish={handleSavePassword}
          style={{ marginTop: '16px' }}
        >
          <Form.Item
            name="currentPassword"
            label={t('changePasswordModal.currentPassword')}
            rules={[
              { required: true, message: t('changePasswordModal.currentPasswordRequired') }
            ]}
          >
            <Input.Password 
              prefix={<LockOutlined />}
              placeholder={t('changePasswordModal.currentPasswordPlaceholder')}
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="newPassword"
            label={t('changePasswordModal.newPassword')}
            rules={[
              { required: true, message: t('changePasswordModal.newPasswordRequired') },
              { min: 8, message: t('changePasswordModal.newPasswordMin') },
              {
                pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
                message: t('changePasswordModal.newPasswordPattern')
              },
              {
                validator: (_, value) => {
                  // Check for common weak passwords
                  const commonPasswords = [
                    'password', '123456', '12345678', 'qwerty', 'abc123', 'password123',
                    'admin', 'letmein', 'welcome', 'monkey', 'dragon', 'master'
                  ];
                  if (commonPasswords.includes(value?.toLowerCase())) {
                    return Promise.reject(new Error(t('changePasswordModal.newPasswordCommon')));
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <Input.Password 
              prefix={<LockOutlined />}
              placeholder={t('changePasswordModal.newPasswordPlaceholder')}
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label={t('changePasswordModal.confirmNewPassword')}
            dependencies={['newPassword']}
            rules={[
              { required: true, message: t('changePasswordModal.confirmNewPasswordRequired') },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error(t('changePasswordModal.passwordsDoNotMatch')));
                },
              }),
            ]}
          >
            <Input.Password 
              prefix={<LockOutlined />}
              placeholder={t('changePasswordModal.confirmNewPasswordPlaceholder')}
              size="large"
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button 
                type="primary" 
                htmlType="submit"
                loading={passwordLoading}
                icon={<SaveOutlined />}
              >
                {t('changePasswordModal.updatePassword')}
              </Button>
              <Button 
                onClick={() => setPasswordModalVisible(false)}
                disabled={passwordLoading}
              >
                {t('changePasswordModal.cancel')}
              </Button>
            </Space>
          </Form.Item>
        </Form>

        <div style={{ 
          padding: 16, 
          backgroundColor: '#f6f8fa', 
          borderRadius: 6,
          marginTop: 16 
        }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t('changePasswordModal.securityNote')}
          </Text>
        </div>
      </Modal>
    </div>
  );
};

export default Profile;
